import React, { useState } from "react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { CardPaymentRequest, paymentAPI, PaymentStatus } from "@/services/payment";

interface PaypalCheckoutProps {
    plan: number;
    email?: string;
    disabled?: boolean;
}

/**
 * Inner component — must be rendered inside a PayPalScriptProvider so that
 * usePayPalScriptReducer has a context to read from.
 */
const PaypalCheckoutInner: React.FC<PaypalCheckoutProps> = ({ plan, email, disabled = false }) => {
    const [message, setMessage] = useState<React.ReactNode>(null);
    const [{ isPending, isRejected }] = usePayPalScriptReducer();

    if (isPending) {
        return <div className="py-4 text-center">Loading PayPal...</div>;
    }
    if (isRejected) {
        return <div className="py-4 text-center text-red-600">Unable to load PayPal.</div>;
    }

    // TypeScript workaround for PayPal components
    const PayPalButtonsComponent = PayPalButtons as React.FC<{
        style?: any;
        createOrder?: () => Promise<string | undefined>;
        onApprove?: (data: any, actions: any) => Promise<any>;
    }>;

    return (
        <div className={`paypal-button-container ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {disabled && (
                <div className="text-sm text-black mb-2">
                    Please accept the EULA to proceed with payment.
                </div>
            )}
            <PayPalButtonsComponent
                    style={{
                        shape: "rect",
                        layout: "vertical",
                        color: "gold",
                        label: "paypal",
                    }}
                    createOrder={async () => {
                        try {
                            console.log("Creating PayPal order for plan:", plan.toString());
                            
                            const orderRequest: CardPaymentRequest = {
                                plan: plan,
                                email: email
                            };

                            const orderData = await paymentAPI.createPaypalOrder(orderRequest);
                            console.log("PayPal order response:", orderData);

                            if (orderData.id) {
                                console.log("PayPal order ID:", orderData.id);
                                return orderData.id;
                            } else {
                                console.error("No order ID in response:", orderData);
                                throw new Error(`No order ID returned. Response: ${JSON.stringify(orderData)}`);
                            }
                        } catch (error) {
                            console.error("PayPal createOrder error:", error);
                            setMessage(`Could not initiate PayPal Checkout...${error}`);
                            throw error; // Re-throw to prevent PayPal from continuing
                        }
                    }}
                    onApprove={async (data: any, actions: any) => {
                        try {
                            console.log("Capturing PayPal order:", data.orderID);
                            
                            const orderData = await paymentAPI.capturePaypalOrder(data.orderID);
                            console.log("PayPal capture response:", orderData);
                            
                            const purchaseUnit = orderData.purchaseUnits[0];
                            if (!purchaseUnit || !purchaseUnit.payments || !purchaseUnit.payments.captures || purchaseUnit.payments.captures.length === 0) {
                                throw new Error("No captures found in purchase unit");
                            }
                            console.log("Purchase unit:", purchaseUnit);
                            const transaction = purchaseUnit.payments.captures[0];
                            console.log("Transaction details:", transaction);
                            console.log("Payment status:", orderData.status);
                            if (orderData?.status === PaymentStatus.VOIDED) {
                                return actions.restart();
                            } else if (orderData?.status !== PaymentStatus.COMPLETED) {
                                throw new Error(
                                    `${orderData?.status} (${orderData.debug_id})`
                                );
                            } else {
                                const item = purchaseUnit.items[0];
                                setMessage(
                                    <div>
                                        <p>
                                            <strong>Transaction completed!</strong>
                                        </p>
                                        <p>
                                            Purchase completed! {item.name} is now available in your account. You will now see {item.name} in <a href="/profile">your account for download</a>.
                                        </p>
                                    </div>
                                );
                                console.log(
                                    "Capture result",
                                    orderData,
                                    JSON.stringify(orderData, null, 2)
                                );
                            }
                        } catch (error) {
                            console.error("PayPal capture error:", error);
                            setMessage(
                                <span>Your transaction could not be processed: {String(error)}</span>
                            );
                        }
                    }}
                />
            {message && <div>{message}</div>}
        </div>
    );
};

/**
 * Public export — wraps the inner component in a single PayPalScriptProvider
 * so the SDK is only loaded once per mount and usePayPalScriptReducer always
 * has a valid context.
 *
 * initialOptions is built here (inside the component) rather than at module
 * scope so that VITE_PAYPAL_CLIENT_ID is read on each render. If it were
 * evaluated at module load time it could cache `undefined` when the env var
 * is not yet available, causing the SDK to request a URL with no client-id
 * and return a 400.
 */
export const PaypalCheckout: React.FC<PaypalCheckoutProps> = (props) => {
    const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;

    if (!clientId) {
        console.error("PaypalCheckout: VITE_PAYPAL_CLIENT_ID is not set. Check your .env file.");
        return (
            <div className="py-4 text-center text-red-600 text-sm">
                PayPal is not configured. Please contact support.
            </div>
        );
    }

    const initialOptions = {
        clientId,
        currency: "USD",
        components: "buttons",
    };

    return (
        <PayPalScriptProvider options={initialOptions}>
            <PaypalCheckoutInner {...props} />
        </PayPalScriptProvider>
    );
};